import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Shepherd from "shepherd.js";
import { offset } from "@floating-ui/dom";
import "shepherd.js/dist/css/shepherd.css";
import { getTourSteps } from "./tourSteps";
import "./onboardingTour.scss";

const TOUR_COMPLETED_KEY = "tour_completed";

const OnboardingTour = () => {
  const { t } = useTranslation();
  const tourRef = useRef<InstanceType<typeof Shepherd.Tour> | null>(null);

  useEffect(() => {
    const alreadyCompleted = localStorage.getItem(TOUR_COMPLETED_KEY) === "true";
    if (alreadyCompleted) return;

    const timer = setTimeout(() => {
      const mobile = window.matchMedia("(max-width: 1023px)").matches;

      const tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          scrollTo: false,
          cancelIcon: { enabled: true },
          modalOverlayOpeningPadding: 4,
          modalOverlayOpeningRadius: 8,
          floatingUIOptions: {
            middleware: [offset(mobile ? { mainAxis: 12 } : { mainAxis: 14, crossAxis: 0 })],
          },
        },
      });

      const steps = getTourSteps(t, mobile);

      steps.forEach((stepConfig, index) => {
        const isFirst = index === 0;
        const isLast = index === steps.length - 1;

        const buttons: any[] = [];

        if (!isFirst) {
          buttons.push({
            text: t("tourBack"),
            action: tour.back,
            classes: "shepherd-button",
          });
        } else {
          buttons.push({
            text: t("tourSkip"),
            action: () => {
              localStorage.setItem(TOUR_COMPLETED_KEY, "true");
              tour.cancel();
            },
            classes: "shepherd-button",
          });
        }

        buttons.push({
          text: isLast ? t("tourFinish") : t("tourNext"),
          action: isLast
            ? () => {
              localStorage.setItem(TOUR_COMPLETED_KEY, "true");
              tour.complete();
            }
            : tour.next,
          classes: "shepherd-button shepherd-button-primary",
        });

        tour.addStep({
          id: stepConfig.id,
          text: stepConfig.text,
          attachTo: {
            element: stepConfig.target,
            on: stepConfig.position as any,
          },
          buttons,
        });
      });

      tour.on("cancel", () => {
        localStorage.setItem(TOUR_COMPLETED_KEY, "true");
      });

      tourRef.current = tour;
      tour.start();
    }, 1500);

    return () => {
      clearTimeout(timer);
      tourRef.current?.cancel();
      tourRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default OnboardingTour;
